import { i18n } from '@lingui/core';
import { ScrollSnapSlider } from '@we-make-websites/ui-lib';
import clsx from 'clsx';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import { useContext } from 'use-context-selector';
import { OverlayContext } from 'components/overlay/OverlayContext';
import { ProductContext } from 'components/productContext/ProductProvider';
import type {
  StoreMediaNode,
  StoreMediaImage,
} from 'components/productContext/types/storeMedia';
import { StoreMediaType } from 'components/productContext/types/storeMedia';
import IconClose from 'icons/misc/close.svg';
import IconPlus from 'icons/misc/plus.svg';
import { MediaItem } from '../mediaItem/MediaItem';
import Overlay from '../overlay/Overlay';
import styles from './ProductGallery.module.scss';

export const ProductGallery = () => {
  const productContext = useContext(ProductContext);
  if (productContext === undefined)
    throw new Error('ProductGallery must be used within a ProductContext');

  const {
    media,
    awardsIcons,
    isLoading: contextLoading,
    selectedVariant,
    variants,
  } = productContext;

  // Toggle Active Header
  const { toggle: toggleOverlay } = useContext(OverlayContext);
  const galleryMedia: StoreMediaNode[] = media.filter((mediaItem) => {
    if (
      mediaItem?.type !== 'IMAGE' ||
      (!mediaItem?.image?.altText.includes('-pack_shot') &&
        !variants.find((variant) => variant.image?.url === mediaItem.image.url))
    ) {
      return mediaItem;
    }
  });

  const variantImage = media.find(
    (mediaItem) =>
      mediaItem.type === 'IMAGE' &&
      selectedVariant?.image?.url === mediaItem.image.url
  );

  if (variantImage) {
    galleryMedia.unshift(variantImage);
  }

  const displayMedia: StoreMediaNode[] = galleryMedia.filter((mediaItem) => {
    const supportedMediaTypes = [StoreMediaType.Image, StoreMediaType.Video];
    return supportedMediaTypes.includes(mediaItem.type);
  });

  // Get first image.
  const featuredMedia = media.find(
    (image) => image.type === StoreMediaType.Image
  ) as StoreMediaImage;

  const selectedMedia = displayMedia[0];

  const isLoading = contextLoading || !selectedMedia;

  return (
    <>
      {featuredMedia && (
        <NextSeo
          openGraph={{
            images: [
              {
                url: featuredMedia.image.url,
                width: featuredMedia.image.width,
                height: featuredMedia.image.height,
                alt: featuredMedia.image.altText,
              },
            ],
          }}
        />
      )}
      <div className={clsx(styles.productGallery, isLoading && 'is-loading')}>
        {awardsIcons.length > 0 && (
          <div className={styles.productGallery__awardsIcons}>
            {awardsIcons.map(
              (awardIcon, index) =>
                awardIcon && (
                  <Image
                    key={index}
                    src={awardIcon}
                    alt={i18n._(
                      /* i18n */ {
                        id: `product.award_icon${index + 1}`,
                        message: `Award icon ${index + 1}`,
                      }
                    )}
                    title={i18n._(
                      /* i18n */ {
                        id: `product.award_icon${index + 1}`,
                        message: `Award icon ${index + 1}`,
                      }
                    )}
                    height="65"
                    width="65"
                    quality={100}
                    unoptimized
                  />
                )
            )}
          </div>
        )}
        <div
          className={styles.productGallery__wrapperMobile}
          data-test-id="productGalleryMobile"
        >
          <ScrollSnapSlider
            dots={true}
            height="100%"
            spacing="0px"
            className={styles.productGalleryMobile}
          >
            {galleryMedia?.map((media) => (
              <MediaItem
                key={media.id}
                media={media}
                active={media.id === selectedMedia?.id}
              />
            ))}
          </ScrollSnapSlider>
        </div>

        <div className={clsx(styles.productGallery__wrapperDesktop)}>
          <ScrollSnapSlider
            reverse={false}
            dots={true}
            height="633px"
            spacing="0px"
          >
            {displayMedia.map((media) => (
              <MediaItem
                key={media.id}
                media={media}
                active={media.id === selectedMedia?.id}
                imageProps={{
                  height: 633,
                  width: 511,
                }}
              />
            ))}
          </ScrollSnapSlider>
        </div>

        <button
          className={styles.productGallery__zoomOpen}
          onClick={() => {
            toggleOverlay({
              ignoreDismissed: true,
              namespace: 'productGalleryZoom',
            });
          }}
          aria-label={i18n._(
            /* i18n */ {
              id: 'productGallery.zoomButton',
              message: 'Open product gallery',
            }
          )}
        >
          <IconPlus />
        </button>

        <Overlay
          type="modal"
          blockClass={'productGalleryZoomOverlay'}
          namespace={'productGalleryZoom'}
          template={false}
          dangerouslyBypassFocusLock={false}
        >
          <button
            className={styles.productGallery__zoomClose}
            aria-label={i18n._(
              /* i18n */ {
                id: 'accessibility.close',
                message: 'Close',
              }
            )}
            onClick={() => toggleOverlay({ namespace: 'productGalleryZoom' })}
          >
            <IconClose />
          </button>

          {displayMedia.map((media) => {
            if (media.type !== StoreMediaType.Image) return null;

            return (
              <MediaItem
                key={media.id}
                media={media}
                imageProps={{
                  height: media.image.height,
                  width: media.image.width,
                  style: { height: 'auto' },
                }}
                active={media.id === selectedMedia?.id}
              />
            );
          })}
        </Overlay>
      </div>
    </>
  );
};
